import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, CheckCheck } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";

// Suggests a rider for each unmatched plate from the driver name in the Aldrees file.
export const FuelSmartMatch = ({ month, provider, vehicles, onDone }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchSuggestions = async () => {
    setBusy(true);
    try {
      const r = await api.fuelPlateSuggestions(month || undefined, provider || undefined);
      setRows(r.suggestions.map((s) => ({ ...s, pick: s.employee_id || "" })));
      if (!r.suggestions.length) toast.info("كل اللوحات مربوطة");
    } catch { toast.error("تعذّر جلب الاقتراحات"); }
    finally { setBusy(false); }
  };

  const approveAll = async () => {
    const items = rows.filter((r) => r.pick).map((r) => ({ plate: r.plate, employee_id: r.pick }));
    if (!items.length) { toast.info("لا توجد اقتراحات لاعتمادها"); return; }
    setBusy(true);
    try {
      const r = await api.fuelPlateMapBulk(items);
      toast.success(`تم ربط ${r.linked} لوحة`);
      setRows(null); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الاعتماد"); }
    finally { setBusy(false); }
  };

  if (!rows) {
    return (
      <Button data-testid="fuel-smart-match-btn" size="sm" variant="outline" disabled={busy} onClick={fetchSuggestions}
        className="gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50 h-8">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} ربط تلقائي ذكي
      </Button>
    );
  }
  const picked = rows.filter((r) => r.pick).length;
  return (
    <div data-testid="fuel-smart-match-panel" className="w-full rounded-xl border border-violet-200 bg-violet-50/40 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-violet-800 font-semibold">اقتراحات من اسم السائق — {picked} من {rows.length} لوحة لها مندوب مقترح</p>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setRows(null)} data-testid="fuel-smart-cancel">إلغاء</Button>
          <Button size="sm" data-testid="fuel-smart-approve-all" disabled={busy || !picked} onClick={approveAll} className="h-8 gap-1.5 bg-violet-600 hover:bg-violet-700">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCheck className="h-4 w-4" />} اعتماد الكل ({picked})
          </Button>
        </div>
      </div>
      <div className="max-h-56 overflow-y-auto space-y-1">
        {rows.map((r, i) => (
          <div key={r.plate} data-testid={`fuel-smart-row-${i}`} className="grid grid-cols-[0.9fr_1.2fr_1.6fr] gap-2 items-center text-xs bg-white rounded-lg border border-slate-100 px-2 py-1.5">
            <span className="font-mono-nums font-bold" dir="ltr">{r.plate}</span>
            <span className="text-slate-500 truncate" title={r.driver_name}>{r.driver_name || "—"}</span>
            <select value={r.pick} onChange={(e) => setRows(rows.map((x) => x.plate === r.plate ? { ...x, pick: e.target.value } : x))}
              data-testid={`fuel-smart-select-${i}`} className={`h-7 rounded-md border px-1.5 ${r.pick ? "border-emerald-300 bg-emerald-50" : "border-slate-200"}`}>
              <option value="">— بدون —</option>
              {vehicles.map((v) => <option key={v.employee_id} value={v.employee_id}>{v.name}{v.vehicle_number ? ` — ${v.vehicle_number}` : ""}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};
